<script setup lang="ts">
import { ref, computed, onMounted, defineProps, watch } from 'vue'
import { storeToRefs } from 'pinia'
import GbButton from '@/components/GbButton.vue'
import GbInlineMessage from '@/components/GbInlineMessage.vue'
import { timeAgo } from '@/util/time'
import LibrarySidebarMenu from './LibrarySidebarMenu.vue'
import NewFolderModal from './NewFolderModal.vue'
import RenameFolderModal from './RenameFolderModal.vue'
import DeleteFolderModal from './DeleteFolderModal.vue'
import ShareFolderModal from './ShareFolderModal.vue'
import UnarchiveFirstModal from './UnarchiveFirstModal.vue'
import ArchiveFolderModal from './ArchiveFolderModal.vue'
import UnarchiveFolderModal from './UnarchiveFolderModal.vue'
import PremiumActionModal from '@/components/PremiumActionModal.vue'
import ConfirmDialog from 'primevue/confirmdialog'
import { useConfirm } from 'primevue/useconfirm'
import { useUserStore } from './../authentication/user.store'
import { useLibraryStore } from './library.store.ts'
import { useContentAccessStore } from '@/content_access/content_access.store.ts'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { LibraryFolder, ViewMode } from './library_types'
import { useRoute, useRouter } from 'vue-router'
import { useLibraryFolderAnalytics } from './composables/useLibraryFolderAnalytics'
import AddContributorsModal from './AddContributorsModal.vue'
import RemoveContributorModal from './RemoveContributorModal.vue'

import ProgressSpinner from 'primevue/progressspinner'
import DataTable from 'primevue/datatable'
import type { DataTableSortEvent } from 'primevue/datatable'
import Column from 'primevue/column'
import { useVisibilityStore } from '@/visibility/visibility.store'
import FolderActionsMenu from './FolderActionsMenu.vue'
import FolderSharedIcon from '@/assets/folder_shared.svg'
import * as constants from './library_constants'
import { EVENT_NAMES } from '@/tracking/analytics/event_names'
import FolderAvatar from './FolderAvatar.vue'

/***************************************************/
/**** Setup related to store, router, analytics ***/
/***************************************************/
interface Props {
  mode: ViewMode
}
const props = defineProps<Props>()
const route = useRoute()
const router = useRouter()
const libraryStore = useLibraryStore()
const userStore = useUserStore()
let analytics = getAnalyticsTracker()
const contentAccessStore = useContentAccessStore()
const confirm = useConfirm()
const visibilityStore = useVisibilityStore()
const {LIBRARY} = EVENT_NAMES

/***************************************************/
/******** Store refs & computed properties *********/
/***************************************************/
const { loadingFolders, loadFoldersError, folders, archivedFolders, folderCount } = storeToRefs(libraryStore)
const userOwnedFolders = computed(() => libraryStore.userOwnedFolders)
const sharedFolders = computed(() => libraryStore.sharedFolders)
const { isTrial, isLicenseExpired } = storeToRefs(userStore)
const getOwnerInfo = (folder: LibraryFolder) => userStore.getFolderMemberInfo(folder)
const folderHasCurrentContributors = (folderData: LibraryFolder): boolean => {
  const contributorRoles = folderData?.folder_roles?.filter(
    role => role.role?.toLowerCase() === 'contributor'
  )
  return contributorRoles ? contributorRoles.length > 0 : false
}
const {LIBRARY_MODE_ALL, LIBRARY_MODE_ARCHIVED, LIBRARY_MODE_OWNED, LIBRARY_MODE_SHARED} = constants
const isAllMode = computed(() => props.mode === LIBRARY_MODE_ALL)
const isArchivedMode = computed(() => props.mode ===  LIBRARY_MODE_ARCHIVED)
const isOwnedMode = computed(() => props.mode === LIBRARY_MODE_OWNED)
const isSharedMode = computed(() => props.mode === LIBRARY_MODE_SHARED)
const shouldShowPremiumAction = computed(() => isTrial.value || isLicenseExpired.value)
const { maxFoldersReached, libraryQuotas } = storeToRefs(contentAccessStore)
const displayedFolders = computed<LibraryFolder[]>(() => {
  switch (props.mode) {
    case LIBRARY_MODE_ALL:
      return folders.value
    case LIBRARY_MODE_ARCHIVED:
      return archivedFolders.value
    case LIBRARY_MODE_OWNED:
      return userOwnedFolders.value
    case LIBRARY_MODE_SHARED:
      return sharedFolders.value
    default:
      return folders.value ?? []
  }
})

/**********************************************/
/******** Fetch data on page load  ************/
/**********************************************/

const loadView = () => {
  if (props.mode === LIBRARY_MODE_ARCHIVED) {
    libraryStore.fetchArchivedFolders(userStore.userId)
  }
  libraryStore.fetchFolders(userStore.userId)
  switch (props.mode) {
    case LIBRARY_MODE_ALL:
      analytics.sendEvent(LIBRARY.VIEW_MODE.ALL)
      break
    case LIBRARY_MODE_ARCHIVED:
      analytics.sendEvent(LIBRARY.VIEW_MODE.ARCHIVED)
      break
    case LIBRARY_MODE_OWNED:
      analytics.sendEvent(LIBRARY.VIEW_MODE.OWNED)
      break
    case LIBRARY_MODE_SHARED:
      analytics.sendEvent(LIBRARY.VIEW_MODE.SHARED)
      break
  }
}

onMounted(() => {
  loadView()
})

watch(() => route.path, () => {
  loadView()
})

/**************************************************/
/********************* Modals *********************/
/**************************************************/
const openNewFolder = ref(false)
const openRename = ref(false)
const openShare = ref(false)
const openDelete = ref(false)
const openUnarchiveFirst = ref(false)
const openArchive = ref(false)
const openUnarchive = ref(false)
const openPremiumAction = ref(false)
const openContributors = ref(false)
const openRemoveContributor = ref(false)

// Watch for AddContributorsModal closing to refetch folders
watch(() => openContributors.value, (newValue, oldValue) => {
  if (oldValue && !newValue) {
    // Modal was closed, refetch folders to get updated contributor information
    libraryStore.fetchFolders(userStore.userId)
  }
})

function openModal(modalName: string, folder?: LibraryFolder) {
  if (folder) {
    libraryStore.currentFolder = folder
  }

  const { getLegacyEventData, getEventData } = useLibraryFolderAnalytics()

  switch (modalName) {
    case 'rename':
      openRename.value = true
      analytics.sendEvent('Clicked Edit Student Button', getLegacyEventData.value) // to be deprecated
      analytics.sendEvent(LIBRARY.FOLDER_ACTION.CLICKED_EDIT_FOLDER, getEventData.value)
      break
    case 'delete':
      openDelete.value = true
      analytics.sendEvent('Clicked Delete Student Button', getLegacyEventData.value) // to be deprecated
      analytics.sendEvent(LIBRARY.FOLDER_ACTION.CLICKED_DELETE_FOLDER, getEventData.value)
      break
    case 'share':
      if (!visibilityStore.shareVisibility) return
      openShare.value = true
      analytics.sendEvent('Clicked Share Student', getLegacyEventData.value) // to be deprecated
      analytics.sendEvent(LIBRARY.FOLDER_ACTION.CLICKED_SHARE_FOLDER, getEventData.value)
      break
    case 'unarchiveFirst':
      openUnarchiveFirst.value = true
      analytics.sendEvent(LIBRARY.FOLDER_ACTION.ATTEMPTED_OPEN_ARCHIVE_FOLDER, getEventData.value)
      break
    case 'archive':
      openArchive.value = true
      analytics.sendEvent(LIBRARY.FOLDER_ACTION.CLICKED_ARCHIVE_FOLDER, getEventData.value)
      break
    case 'unarchive':
      openUnarchive.value = true
      analytics.sendEvent(LIBRARY.FOLDER_ACTION.CLICKED_UNARCHIVE_FOLDER, getEventData.value)
      break
    case 'premiumAction':
      openPremiumAction.value = true
      break
    case 'contributors':
      openContributors.value = true
      analytics.sendEvent(LIBRARY.FOLDER_ACTION.OPENED_SHARE_FOLDER_MODAL, getEventData.value)
      break
    case 'removeSelfAsContributor':
      openRemoveContributor.value = true
      break
  }
}

/**********************************************/
/************** Sorting helpers ***************/
/**********************************************/
const sortField = ref<string | undefined>('name')
const sortOrder = ref<1 | -1 | undefined>(1)

function sortIcon(field: string): string {
  if (sortField.value === field) {
    return sortOrder.value === 1 ? 'arrow_upward' : 'arrow_downward'
  }
  return ''
}

function onSort(event: DataTableSortEvent): void {
  sortField.value = typeof event.sortField === 'string' ? event.sortField : undefined
  sortOrder.value = typeof event.sortOrder === 'string' ? event.sortOrder : undefined
}
function triggerSort(fieldName: string | undefined): void {
  sortField.value = fieldName ?? 'name'
  sortOrder.value = sortOrder.value === 1 ? -1 : 1
}

/**********************************************/
/************** Folder Actions ***************/
/**********************************************/
type FolderAction = 'Add Folder' | 'Unarchive Folder'
interface ConfirmServiceOptions {
  message: string
  action: FolderAction
}

function handleFolderQuotaExceeded(options: ConfirmServiceOptions) {
  confirm.require({
    group: 'max-folders',
    header: 'Folder Limit',
    message: options.message
  })
  analytics.sendEvent('Content Access Blocked', {
    blocked_action: options.action,
    blocking_quota: 'max_folders',
    blocking_count: folderCount.value,
    blocking_quota_value: libraryQuotas.value.maxFolders
  })
}

function addNewFolder() {
  if (maxFoldersReached.value) {
    handleFolderQuotaExceeded({
      message: 'You have reached the maximum number of folders. You must delete or archive an existing folder to add a new one.',
      action: 'Add Folder'
    })
    return
  }

  openNewFolder.value = true
  analytics.sendEvent('Clicked Add Student Button') // to be deprecated
  analytics.sendEvent(LIBRARY.FOLDER_ACTION.CLICKED_ADD_FOLDER)
}

function unarchiveFolder(folder: LibraryFolder) {
  if (maxFoldersReached.value) {
    handleFolderQuotaExceeded({
      message: 'You have reached your active folder limit. You must delete or archive active folders before unarchiving this one.',
      action: 'Unarchive Folder'
    })
    return
  }

  openModal('unarchive', folder)
}

const onRowClick = ({ data }: { data: LibraryFolder }) => {
  if (isAllMode.value || isOwnedMode.value || isSharedMode.value) {
    router.push({ name: 'LibraryFolder', params: { uuid: data.uuid } });
  } else {
    openModal('unarchiveFirst', data);
  }
};

/****************************************************/
/****************** Other Helpers *******************/
/****************************************************/
function getHeaderTitle() {
  switch (props.mode) {
    case LIBRARY_MODE_ALL:
      return { text: 'All Folders', hasBold: false }
    case LIBRARY_MODE_ARCHIVED:
      return { text: 'Archived Folders', hasBold: false }
    case LIBRARY_MODE_OWNED:
      return { text: 'All Folders: ', boldText: 'My Folders', hasBold: true }
    case LIBRARY_MODE_SHARED:
      return { text: 'All Folders: ', boldText: 'Shared Folders', hasBold: true }
    default:
      return { text: 'All Folders', hasBold: false }
  }
}

</script>
<template>
  <div class="container">
    <div class="grid grid-cols-12 gap-4 pt-12">
      <div class="col-span-12 py-4 pl-4 pr-8 md:col-span-4 lg:col-span-3">
        <LibrarySidebarMenu />
      </div>
      <div class="col-span-12 p-4 md:col-span-8 lg:col-span-9">
        <div class="flex justify-between">
          <h1 class="header-md pl-4 font-medium">
            <template v-if="getHeaderTitle().hasBold">
              {{ getHeaderTitle().text }}<strong>{{ getHeaderTitle().boldText }}</strong>
            </template>
            <template v-else>
              {{ getHeaderTitle().text }}
            </template>
          </h1>
          <GbButton
            v-if="isAllMode || isOwnedMode || isSharedMode"
            label="New Folder"
            gb-theme="bluePrimary"
            @click="addNewFolder()"
          />
        </div>
        <div class="relative pt-2">
          <div v-if="loadingFolders" class="pl-4 inline-flex items-center">
            <ProgressSpinner strokeWidth="8" class="h-5 w-5 stroke-primary-400" />
            <span class="ml-2">Loading resources...</span>
          </div>
          <GbInlineMessage gbTheme="grayDark" class="ml-4 mt-5" v-else-if="loadFoldersError">
            <template #icon><span class="material-icons-round"> info_outline </span></template>
            We couldn't load the folders. Try again later.
          </GbInlineMessage>
          <div v-else>
            <DataTable
              size="small"
              v-if="displayedFolders.length"
              :value="displayedFolders"
              :sortField="sortField"
              :sortOrder="sortOrder"
              @sort="onSort"
              selectionMode="single"
              @row-select="onRowClick"
            >
              <!---------------------->
              <!----- NAME COLUMN ---->
              <!---------------------->
              <Column field="name">
                <template #header="{}">
                  <div class="flex items-center justify-between">
                    <GbButton gbTheme="grayTertiary" @click="triggerSort('name')" size="small">
                      <span>Name</span>
                      <span class="material-icons-round text-lg">
                        {{ sortIcon('name') }}
                      </span>
                    </GbButton>
                  </div>
                </template>
                <template #body="{ data }">
                  <div class="flex items-center">
                    <div class="w-6 h-6 mr-2 flex items-center justify-center">
                      <img v-if="folderHasCurrentContributors(data)" :src="FolderSharedIcon" class="w-6 h-6" alt="Shared folder" />
                      <span v-else class="material-icons-round text-gray-700">folder</span>
                    </div>
                    <span class="text-md fs-exclude">{{ data.name }}</span>
                  </div>
                </template>
              </Column>
              <!------------------------>
              <!----- OWNER COLUMN ----->
              <!------------------------>
              <Column field="owner">
                <template #header="{}">
                  <div class="flex items-center justify-between">
                    <GbButton gbTheme="grayTertiary" @click="triggerSort('owner')" size="small">
                      <span>Owner</span>
                      <span class="material-icons-round text-lg">
                        {{ sortIcon('owner') }}
                      </span>
                    </GbButton>
                  </div>
                </template>
                <!----------------------->
                <!-- OWNER COLUMN BODY -->
                <!----------------------->
                <template #body="{ data }">
                  <div class="flex items-center">
                    <!-- OWNER AVATAR -->
                    <FolderAvatar :userId="data.owner" :folder="data" avatarSize="md" />
                    <!-- OWNER NAME -->
                    <span class="text-md fs-exclude">
                      {{ getOwnerInfo(data)?.[data.owner]?.currentUserIsOwner ? 'Me' : getOwnerInfo(data)?.[data.owner]?.name }}
                    </span>
                  </div>
                </template>
              </Column>
              <!--------------------->
              <!--UPDATED AT COLUMN-->
              <!--------------------->
              <Column v-if="!isArchivedMode" field="updated_at" dataType="date" class="w-3/12">
                <template #header="{}">
                  <div class="flex items-center justify-end">
                    <GbButton
                      gbTheme="grayTertiary"
                      @click="triggerSort('updated_at')"
                      size="small"
                      :class="sortIcon('updated_at') ? '' : 'mr-4'"
                    >
                      <span>Last Updated</span>
                      <span class="material-icons-round text-lg">
                        {{ sortIcon('updated_at') }}
                      </span>
                    </GbButton>
                  </div>
                </template>
                <template #body="{ data }">
                  <div class="flex items-center justify-end">
                    <span class="text-md">{{ timeAgo(data.updated_at) }}</span>
                    <FolderActionsMenu
                      :folder="data"
                      :is-archive-mode="isArchivedMode"
                      :should-show-premium-action="shouldShowPremiumAction"
                      @rename="openModal('rename', data)"
                      @share="openModal('share', data)"
                      @archive="openModal('archive', data)"
                      @delete="openModal('delete', data)"
                      @premium-action="openModal('premiumAction')"
                      @contributors="openModal('contributors', data)"
                      @remove-self-as-contributor="openModal('removeSelfAsContributor', data)"
                      class="ml-2"
                    />
                  </div>
                </template>
              </Column>
              <Column v-else-if="isArchivedMode" field="archived_at" dataType="date" class="w-3/12">
                <template #header="{}">
                  <div class="flex items-center justify-end">
                    <GbButton
                      gbTheme="grayTertiary"
                      @click="triggerSort('archived_at')"
                      size="small"
                      :class="sortIcon('archived_at') ? '' : 'mr-4'"
                    >
                      <span>Archived</span>
                      <span class="material-icons-round text-lg">
                        {{ sortIcon('archived_at') }}
                      </span>
                    </GbButton>
                  </div>
                </template>
                <template #body="{ data }">
                  <div class="flex items-center justify-end">
                    <span class="text-md">{{ timeAgo(data.archived_at) }}</span>
                    <FolderActionsMenu
                      :folder="data"
                      :is-archive-mode="isArchivedMode"
                      :should-show-premium-action="shouldShowPremiumAction"
                      @unarchive="unarchiveFolder(data)"
                      @delete="openModal('delete', data)"
                      @premium-action="openModal('premiumAction')"
                      class="ml-2"
                    />
                  </div>
                </template>
              </Column>
            </DataTable>
            <div v-else class="ml-4 mt-6">
              <span v-if="isAllMode">Create a New Folder and start saving content.</span>
              <span v-else-if="isArchivedMode">You have no archived folders</span>
              <span v-else-if="isOwnedMode">You have no folders of your own</span>
              <span v-else-if="isSharedMode">You have no shared folders</span>
            </div>
          </div>
        </div>
      </div>
      <NewFolderModal
        v-if="isAllMode || isOwnedMode || isSharedMode"
        v-model:externalOpen="openNewFolder"
      />
      <RenameFolderModal
        v-if="isAllMode || isOwnedMode"
        v-model:externalOpen="openRename"
      />
      <DeleteFolderModal
        v-if="isAllMode || isOwnedMode || isArchivedMode"
        v-model:external-open="openDelete"
      />
      <ShareFolderModal
        v-if="(isAllMode || isOwnedMode || isSharedMode) && visibilityStore.shareVisibility && openShare"
        v-model:external-open="openShare"
      />
      <ArchiveFolderModal
        v-if="isAllMode || isOwnedMode"
        v-model:external-open="openArchive"
      />
      <UnarchiveFolderModal
        v-if="isArchivedMode"
        v-model:external-open="openUnarchive"
      />
      <UnarchiveFirstModal
        v-if="isArchivedMode"
        v-model:externalOpen="openUnarchiveFirst"
      />
      <PremiumActionModal
        v-model:external-open="openPremiumAction"
      />
      <AddContributorsModal
        v-model:external-open="openContributors"
        @open-share="openShare = true"
      />
      <RemoveContributorModal
        v-model:externalOpen="openRemoveContributor"
        :userUuid="userStore.userId"
      />
      <ConfirmDialog group="max-folders" class="w-auto md:w-[38rem]">
        <template #container="{ message, rejectCallback }">
          <div class="flex flex-col rounded-md bg-surface-50">
            <div class="mt-2 flex justify-between border-b border-gray-300 p-3">
              <span class="block pl-3 text-xl font-medium">{{ message.header }}</span>
              <GbButton
                size="small"
                rounded
                :icon-only="true"
                aria-label="close dialog"
                gbTheme="grayTertiary"
                class="mr-4 self-start"
                @click="rejectCallback"
              >
                <span class="material-icons-round">close</span>
              </GbButton>
            </div>
            <p class="py-5 pl-7 pr-12 text-gray-700">{{ message.message }}</p>
            <div class="flex items-center justify-end gap-2 border-t border-gray-300 p-4">
              <GbButton label="OK" @click="rejectCallback" gbTheme="bluePrimary"></GbButton>
            </div>
          </div>
        </template>
      </ConfirmDialog>
    </div>
  </div>
</template>
